import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Send, 
  Bot, 
  User, 
  Lightbulb, 
  Edit3, 
  MessageCircle,
  Copy,
  Check,
  AlertCircle,
  X,
  Minimize2,
  Maximize2,
  CheckCircle,
  XCircle,
  Eye,
  Plus,
  Trash2,
  Edit3 as EditIcon
} from 'lucide-react';
import aiService from '../services/aiService';
import contextService from '../services/contextService';
import { useTheme } from '../context/ThemeContext';

const SidebarChatContainer = styled(motion.div)`
  width: 350px;
  height: 100%;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  border-left: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  display: flex;
  flex-direction: column;
  position: relative;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
`;

const ChatHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
`;

const ChatTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#cccccc' : '#24292e'};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModelSelect = styled.select`
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};
  background: ${p => p.theme.mode === 'dark' ? '#0f172a' : '#f8fafc'};
  color: ${p => p.theme.mode === 'dark' ? '#e5e7eb' : '#111827'};
  font-size: 12px;
`;

const ChatControls = styled.div`
  display: flex;
  gap: 8px;
`;

const ControlButton = styled(motion.button)`
  background: none;
  border: none;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#30363d' : '#f6f8fa'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const ChatArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${props => props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.mode === 'dark' ? '#484f58' : '#a8b2ba'};
  }
`;

const SuggestionsBar = styled.div`
  padding: 12px;
  border-bottom: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  background: ${props => props.theme.mode === 'dark' ? '#0f172a' : '#f8fafc'};
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SuggestionButton = styled(motion.button)`
  background: ${props => props.theme.mode === 'dark' ? '#111827' : '#ffffff'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};
  color: ${props => props.theme.mode === 'dark' ? '#d1d5db' : '#374151'};
  padding: 6px 10px;
  border-radius: 8px;
  font-size: 12px;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    background: ${props => props.theme.mode === 'dark' ? '#1f2937' : '#f1f5f9'};
  }
`;

const MessageContainer = styled(motion.div)`
  display: flex;
  gap: 8px;
  align-items: flex-start;
  margin-bottom: 4px;
`;

const MessageAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: ${props => props.$isUser 
    ? (props.theme.mode === 'dark' ? '#0969da' : '#0969da') 
    : (props.theme.mode === 'dark' ? '#30363d' : '#f6f8fa')
  };
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.$isUser ? '#ffffff' : (props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e')};
  flex-shrink: 0;
  border: ${props => props.$isUser ? 'none' : `1px solid ${props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'}`};
`;

const MessageContent = styled.div`
  flex: 1;
  background: ${props => props.$isUser 
    ? (props.theme.mode === 'dark' ? '#0969da' : '#0969da') 
    : (props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa')
  };
  color: ${props => props.$isUser 
    ? '#ffffff' 
    : (props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e')
  };
  padding: 8px 12px;
  border-radius: 12px;
  font-size: 14px;
  line-height: 1.5;
  position: relative;
  white-space: pre-wrap;
  word-wrap: break-word;
  border: ${props => props.$isUser ? 'none' : `1px solid ${props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'}`};
  
  &::after {
    content: '';
    display: ${props => props.$isStreaming ? 'inline-block' : 'none'};
    width: 2px;
    height: 16px;
    background: ${props => props.$isUser ? '#ffffff' : (props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e')};
    margin-left: 2px;
    animation: blink 1s infinite;
  }
  
  @keyframes blink {
    0%, 50% { opacity: 1; }
    51%, 100% { opacity: 0; }
  }
`;

const MessageActions = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 6px;
`;

const ActionButton = styled(motion.button)`
  background: ${props => props.theme.mode === 'dark' ? 'rgba(240, 246, 252, 0.1)' : 'rgba(36, 41, 46, 0.1)'};
  border: none;
  color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.mode === 'dark' ? 'rgba(240, 246, 252, 0.2)' : 'rgba(36, 41, 46, 0.2)'};
    color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  }
`;

const InputArea = styled.div`
  padding: 12px;
  border-top: 1px solid ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  background: ${props => props.theme.mode === 'dark' ? '#1e1e1e' : '#ffffff'};
`;

const InputContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: flex-end;
`;

const TextArea = styled.textarea`
  flex: 1;
  background: ${props => props.theme.mode === 'dark' ? '#0d1117' : '#f6f8fa'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0'};
  border-radius: 8px;
  padding: 8px 12px;
  color: ${props => props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e'};
  font-size: 14px;
  line-height: 1.5;
  resize: none;
  min-height: 36px;
  max-height: 120px;
  font-family: inherit;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.mode === 'dark' ? '#0969da' : '#0969da'};
    box-shadow: 0 0 0 2px ${props => props.theme.mode === 'dark' ? 'rgba(9, 105, 218, 0.3)' : 'rgba(9, 105, 218, 0.3)'};
  }
  
  &::placeholder {
    color: ${props => props.theme.mode === 'dark' ? '#8b949e' : '#586069'};
  }
`;

const SendButton = styled(motion.button)`
  background: ${props => props.theme.mode === 'dark' ? '#0969da' : '#0969da'};
  border: none;
  color: white;
  padding: 8px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 36px;
  height: 36px;
  transition: all 0.2s ease;
  
  &:hover:not(:disabled) {
    background: ${props => props.theme.mode === 'dark' ? '#0550ae' : '#0550ae'};
  }
  
  &:disabled {
    background: ${props => props.theme.mode === 'dark' ? '#484f58' : '#a8b2ba'};
    cursor: not-allowed;
  }
`;


const ModeSelector = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 8px;
`;

const ModeButton = styled(motion.button)`
  background: ${props => props.$active 
    ? (props.theme.mode === 'dark' ? '#0969da' : '#0969da') 
    : (props.theme.mode === 'dark' ? '#21262d' : '#f6f8fa')
  };
  color: ${props => props.$active 
    ? '#ffffff' 
    : (props.theme.mode === 'dark' ? '#f0f6fc' : '#24292e')
  };
  border: 1px solid ${props => props.$active 
    ? (props.theme.mode === 'dark' ? '#0969da' : '#0969da') 
    : (props.theme.mode === 'dark' ? '#30363d' : '#d1d9e0')
  };
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.active 
      ? (props.theme.mode === 'dark' ? '#0550ae' : '#0550ae') 
      : (props.theme.mode === 'dark' ? '#30363d' : '#f6f8fa')
    };
  }
`;

const ProgressContainer = styled(motion.div)`
  margin: 12px 16px;
  padding: 12px;
  background: ${props => props.theme.mode === 'dark' ? 'rgba(10, 132, 255, 0.1)' : 'rgba(10, 132, 255, 0.05)'};
  border: 1px solid ${props => props.theme.mode === 'dark' ? 'rgba(10, 132, 255, 0.3)' : 'rgba(10, 132, 255, 0.2)'};
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ProgressHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ProgressIcon = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #0A84FF;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: 600;
`;

const ProgressText = styled.div`
  flex: 1;
`;

const ProgressTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.mode === 'dark' ? '#ffffff' : '#000000'};
  margin-bottom: 2px;
`;

const ProgressDescription = styled.div`
  font-size: 10px;
  color: ${props => props.theme.mode === 'dark' ? '#a0a0a0' : '#666666'};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background: ${props => props.theme.mode === 'dark' ? '#333333' : '#e1e4e8'};
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #0A84FF, #7C3AED);
  border-radius: 2px;
`;

const StageBadges = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
`;

const StageBadge = styled.div`
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 10px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${props => props.active ? '#0A84FF' : (props.theme.mode === 'dark' ? '#333333' : '#e1e4e8')};
  color: ${props => props.active ? 'white' : (props.theme.mode === 'dark' ? '#a0a0a0' : '#666666')};
  transition: all 0.3s ease;
`;

const CheckIcon = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #10B981;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 8px;
`;

const SidebarChat = ({ 
  isOpen, 
  onClose, 
  mode, 
  onModeChange, 
  content, 
  selectedText,
  onResponse,
  onMinimize,
  isMinimized 
}) => {
  const { theme } = useTheme();
  const DEBUG = false; // 로그 제어 플래그
  const params = (typeof window !== 'undefined') ? new URLSearchParams(window.location.search) : null;
  const previewGuest = params?.get('preview') === '1';
  const [messages, setMessages] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameValue, setRenameValue] = useState('');
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [copiedIndex, setCopiedIndex] = useState(-1);
  const [progress, setProgress] = useState(null);
  const chatAreaRef = useRef(null);
  const textareaRef = useRef(null);

  // 진행 상태 콜백 처리
  const handleProgress = (progressData) => {
    setProgress(progressData);
    
    // 완료된 경우 3초 후 진행 상태 초기화
    if (progressData.status === 'complete') {
      setTimeout(() => {
        setProgress(null);
      }, 3000);
    }
  };

  // 초기화/모드 변경: 현재 세션 메시지 로드 (모드별 필터)
  useEffect(() => {
    if (!isOpen) return;
    const currentSession = contextService.getCurrentSession();
    if (currentSession) {
      const filtered = (currentSession.messages || []).filter(m => !m.mode || m.mode === mode);
      
      // selectedText가 있고 edit 모드일 때는 선택된 텍스트를 알려주는 메시지를 추가
      if (selectedText && mode === 'edit') {
        // 이미 같은 selectedText로 메시지가 있는지 확인 (마지막 메시지가 선택된 텍스트 안내 메시지인지 체크)
        const hasSelectionMessage = filtered.length > 0 && 
          filtered[filtered.length - 1].content && 
          filtered[filtered.length - 1].content.includes('**선택된 텍스트:**');
        
        // 마지막 메시지가 선택된 텍스트 안내 메시지가 아니거나, 다른 텍스트를 선택한 경우에만 추가
        if (!hasSelectionMessage) {
          const selectionMessage = {
            id: Date.now(),
            type: 'assistant',
            content: `다음 선택된 텍스트를 수정해드리겠습니다:\n\n**선택된 텍스트:**\n"${selectedText}"\n\n이 텍스트를 어떻게 수정하고 싶으신가요?`
          };
          setMessages(filtered.length > 0
            ? [...filtered, selectionMessage]
            : [selectionMessage]
          );
        } else {
          // 이미 선택된 텍스트 메시지가 있으면 기존 메시지만 업데이트
          setMessages(filtered);
        }
      } else {
        setMessages(filtered.length > 0
          ? filtered
          : [{ id: 1, type: 'assistant', content: (aiService.isConfigured()
                ? `안녕하세요! ${mode === 'write' ? '글 작성' : mode === 'edit' ? '글 수정' : '질문 답변'}을 도와드리겠습니다. 무엇을 도와드릴까요?`
                : 'API 키가 설정되지 않았습니다. 설정에서 API 키를 입력해주세요. 시뮬레이션된 응답을 제공합니다.')
            }]
        );
      }
    }
  }, [isOpen, mode, selectedText]);

  // 스크롤을 맨 아래로
  useEffect(() => {
    if (chatAreaRef.current) {
      chatAreaRef.current.scrollTop = chatAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // 열릴 때/모드 변경 시 입력창 자동 포커스
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isOpen, mode]);

  // 예시 프롬프트
  const examplePrompts = (() => {
    const common = [
      '핵심만 간결하게 요약해줘 (3~5문장)',
      '근거와 출처(연도/기관 포함)를 덧붙여 더 상세하게 개선해줘',
    ];
    if (mode === 'write') {
      return [
        '주제 개요/연구 목적/기대효과를 포함한 서론을 1200자 이상 작성해줘',
        '목차(서론/이론/방법/결과/논의/결론)를 생성하고 각 절 개요를 3~4문장으로 써줘',
        '최신 통계 3개와 사례 2개를 포함해 학술 톤으로 본문 초안을 2000자 이상 작성해줘',
        ...common,
      ];
    }
    if (mode === 'edit') {
      const editBase = [
        '논리 구조를 재정렬하고 중복/군더더기를 제거해 가독성을 높여줘',
        '문체를 학술 보고서 톤으로 정제하고 문장 길이를 균형 있게 조정해줘',
        '핵심 주장과 근거의 연결을 강화하고 부족한 부분에 보완 설명을 추가해줘',
        '핵심 포인트 5개로 불릿 요약을 추가해줘',
      ];
      if (selectedText && selectedText.length > 0) {
        return [
          '선택한 부분을 표로 정리해줘 (열: 항목, 값, 단위, 비고)',
          '선택한 수치를 막대그래프로 시각화해줘 (x: 항목, y: 값, 단위 표기)',
          '선택한 문단을 더 명확하고 간결한 문장으로 다듬어줘',
          ...editBase,
        ];
      }
      return editBase;
    }
    // question
    return [
      '주요 주장에 대한 반론 가능성과 대응 논리를 정리해줘',
      '추가 검증이 필요한 데이터/문헌을 제안하고 구체적 출처 예시를 알려줘',
      '핵심 내용을 5개 불릿으로 요약하고 실행 가능한 액션아이템 3개를 제안해줘',
      ...common,
    ];
  })();

  const sendExample = async (prompt) => {
    setInput(prompt);
    // 다음 틱에 전송 (state 반영 보장)
    setTimeout(() => {
      handleSend();
    }, 0);
  };
  
  const hasUserMessages = messages && messages.some(m => m.type === 'user');

  // 세션 조작 핸들러들
  const handleNewSession = () => {
    const session = contextService.newSession();
    setSessions(contextService.listSessions());
    // 새 세션은 비어 있으므로 현재 모드 초기 메시지로 세팅
    setMessages([{ id: 1, type: 'assistant', content: (selectedText && mode === 'edit')
      ? `다음 선택된 텍스트를 수정해드리겠습니다:\n\n**선택된 텍스트:**\n"${selectedText}"\n\n이 텍스트를 어떻게 수정하고 싶으신가요?`
      : (aiService.isConfigured()
          ? `안녕하세요! ${mode === 'write' ? '글 작성' : mode === 'edit' ? '글 수정' : '질문 답변'}을 도와드리겠습니다. 무엇을 도와드릴까요?`
          : 'API 키가 설정되지 않았습니다. 설정에서 API 키를 입력해주세요. 시뮬레이션된 응답을 제공합니다.')
    }]);
  };

  const handleSelectSession = (sessionId) => {
    if (!sessionId) return;
    contextService.selectSession(sessionId);
    const currentSession = contextService.getCurrentSession();
    const filtered = (currentSession?.messages || []).filter(m => !m.mode || m.mode === mode);
    setMessages(filtered);
  };

  const handleRenameSession = () => {
    const current = contextService.getCurrentSession();
    if (!current) return;
    setIsRenaming(true);
    setRenameValue(current.title || '대화');
  };

  const commitRename = () => {
    const current = contextService.getCurrentSession();
    if (!current) { setIsRenaming(false); return; }
    contextService.updateSessionTitle(current.id, (renameValue || current.title));
    setSessions(contextService.listSessions());
    setIsRenaming(false);
  };

  const handleDeleteSession = (sessionId) => {
    if (!sessionId) return;
    contextService.deleteSession(sessionId);
    setSessions(contextService.listSessions());
    const current = contextService.getCurrentSession();
    const filtered = (current?.messages || []).filter(m => !m.mode || m.mode === mode);
    setMessages(filtered);
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: input.trim(),
      mode
    };

    setMessages(prev => [...prev, userMessage]);
    // 세션에 저장
    contextService.addMessage({ type: 'user', content: userMessage.content, mode });
    const userInput = input.trim();
    setInput('');
    setIsLoading(true);

    // AI 응답 메시지를 미리 생성 (스트리밍용)
    const aiMessageId = Date.now() + 1;
    const isJsonPatchMode = mode === 'edit' && !selectedText;
    const assistantMessage = {
      id: aiMessageId,
      type: 'assistant',
      content: isJsonPatchMode ? '수정 적용 중...' : 'One-Line Paper 생각 중...',
      isStreaming: true,
      mode
    };

    setMessages(prev => [...prev, assistantMessage]);

    try {
      if (previewGuest) {
        // 미리보기 모드: 간단한 소개 응답만 표시, 실제 호출/적용 없음
        const brief = mode === 'write'
          ? '소개: 작성 모드에서는 주제에 맞춰 IEEE 구조로 긴 문서를 생성합니다. (미리보기에서는 요약만 표시)'
          : mode === 'edit'
          ? '소개: 수정 모드에서는 선택한 영역만 맥락에 맞게 정밀 교정합니다. (미리보기에서는 요약만 표시)'
          : '소개: 질문 모드에서는 문서 맥락 기반으로 한국어로 답변합니다. (미리보기에서는 요약만 표시)';
        setMessages(prev => prev.map(msg => 
          msg.id === aiMessageId 
            ? { ...msg, content: brief, isStreaming: false }
            : msg
        ));
        setIsLoading(false);
        return;
      }
      if (aiService.isConfigured()) {
        if (DEBUG) console.log('스트리밍 API 호출 시작...');
        
        // 스트리밍 콜백 함수
        const onChunk = (chunk) => {
          if (isJsonPatchMode) {
            // JSON 패치 모드: 스트리밍 표시 억제
            return;
          }
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: (msg.content === 'One-Line Paper 생각 중...' ? '' : msg.content) + chunk }
              : msg
          ));
        };

        // 스트리밍 API 호출
        let fullResponse = '';
        const onChunkWithPreview = (chunk) => {
          fullResponse += chunk;
          onChunk(chunk);
        };

        try {
          switch (mode) {
          case 'write':
            // WordEditor에 글이 있으면 기존 문서를 바탕으로 작성
            const hasExistingContent = content && content.trim().length > 0;
            let writePrompt = userInput;
            if (hasExistingContent) {
              writePrompt = `기존 문서 내용을 바탕으로 다음 요청사항을 작성해주세요:\n\n${userInput}\n\n주의: 기존 문서의 스타일, 톤, 구조를 유지하면서 추가하거나 보완해주세요.`;
            }
            await aiService.generateContentStream(writePrompt, {}, onChunkWithPreview, handleProgress);
            break;
          case 'edit':
            // 선택된 텍스트가 있으면: 선택 영역만 수정 출력
            if (selectedText) {
              const selectionPrompt = `선택된 부분만 문맥에 맞춰 수정해주세요. 전체 문서는 컨텍스트로 제공하며, 출력은 "수정된 선택 영역 텍스트"만 반환하세요.\n\n사용자 요청: ${userInput}\n\n[선택된 텍스트]\n${selectedText}\n\n주의: 전체 문서 재작성 금지. 선택 구간만 자연스럽게 개선.`;
              await aiService.editContentStream(content, selectionPrompt, {}, onChunkWithPreview);
            } else {
              // 선택이 없을 경우: 문서 내 위치를 스스로 찾아 최소 변경 JSON 패치로 반환
              const jsonPatchPrompt = `문서 전체를 컨텍스트로 참고하여, 사용자 요청을 반영해 필요한 부분만 최소 범위로 수정하세요.\n\n반드시 JSON만 출력하세요(설명문, 코드펜스 금지). 형식은 배열입니다. 가능한 연산 타입:\n- {"type":"replace","target":"원문에서 교체할 정확한 일부 또는 섹션 제목","replacement":"대체 텍스트"}\n- {"type":"insert_after","target":"기준이 되는 텍스트(예: 섹션 제목)","content":"추가할 텍스트"}\n- {"type":"insert_before","target":"기준이 되는 텍스트","content":"추가할 텍스트"}\n\n규칙:\n- 문서의 톤/스타일/구조를 유지\n- target은 문서에서 유일하게 식별 가능한 짧은 구절로 지정\n- 불필요한 광범위 재작성 금지\n- JSON 외 다른 텍스트 출력 금지\n\n사용자 요청: ${userInput}`;
              await aiService.editContentStream(content, jsonPatchPrompt, {}, onChunkWithPreview);
            }
            break;
          case 'question':
            await aiService.answerQuestionStream(userInput, content, {}, onChunkWithPreview);
            break;
          default:
            await aiService.generateContentStream(userInput, {}, onChunkWithPreview, handleProgress);
          }
        } catch (err) {
          // 포인트 부족 등 오류 표시
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: `오류: ${err.message}`, isStreaming: false }
              : msg
          ));
          setIsLoading(false);
          return;
        }

        // 스트리밍 완료 후 에디터 반영/표시 처리
        onResponse(fullResponse, mode);
        if (isJsonPatchMode) {
          // JSON 패치 결과는 화면에 원문 JSON을 노출하지 않고 요약만 표시
          let appliedCount = 0;
          try {
            const trimmed = (fullResponse || '').trim().replace(/^```json|^```/i, '').replace(/```$/,'').trim();
            const patches = JSON.parse(trimmed);
            if (Array.isArray(patches)) appliedCount = patches.length;
          } catch {}
          const summary = appliedCount > 0 ? `자동 적용 완료: ${appliedCount}건` : '자동 적용 완료';
          setMessages(prev => prev.map(msg => msg.id === aiMessageId ? { ...msg, content: summary } : msg));
          contextService.addMessage({ type: 'assistant', content: summary, mode });
        } else {
          // 일반 모드: 원문 표시
          contextService.addMessage({ type: 'assistant', content: fullResponse, mode });
        }
      } else {
        if (DEBUG) console.log('시뮬레이션된 응답 사용 (스트리밍 효과)');
        
        // 시뮬레이션 응답을 스트리밍처럼 표시
        let simulatedResponse;
        if (mode === 'edit' && selectedText) {
          simulatedResponse = `선택된 텍스트를 수정했습니다:\n\n"${selectedText}"\n\n→ 수정된 내용:\n\n${selectedText.replace(/조직/g, '효율적인 조직').replace(/관리/g, '체계적 관리').replace(/방법/g, '혁신적 방법')}\n\n이렇게 수정하는 것이 어떠신가요? 추가로 개선하고 싶은 부분이 있다면 말씀해 주세요.`;
        } else {
          // write 모드: 기존 문서가 있으면 바탕으로 작성
          const hasExistingContent = content && content.trim().length > 0;
          const simulatedInput = hasExistingContent 
            ? `기존 문서 내용을 바탕으로 다음 요청사항을 작성해주세요:\n\n${userInput}\n\n주의: 기존 문서의 스타일, 톤, 구조를 유지하면서 추가하거나 보완해주세요.`
            : userInput;
          simulatedResponse = aiService.getSimulatedResponse(mode, simulatedInput, content);
        }
        
        // 시뮬레이션 응답을 단어별로 나누어 스트리밍 효과 생성
        const words = simulatedResponse.split(' ');
        let currentContent = '';
        
        for (let i = 0; i < words.length; i++) {
          currentContent += (i > 0 ? ' ' : '') + words[i];
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: currentContent }
              : msg
          ));
          
          // 각 단어마다 30ms 대기 (자연스러운 타이핑 효과)
          await new Promise(resolve => setTimeout(resolve, 30));
        }

        // 시뮬레이션 완료 후 미리보기 설정
        onResponse(simulatedResponse, mode);
        contextService.addMessage({ type: 'assistant', content: simulatedResponse, mode });
      }

      // 스트리밍 완료 표시
      setMessages(prev => prev.map(msg => 
        msg.id === aiMessageId 
          ? { ...msg, isStreaming: false }
          : msg
      ));
      
      // 에디터 반영은 onResponse(fullResponse, mode)로 이미 처리됨
    } catch (error) {
      console.error('AI 응답 오류:', error);
      setMessages(prev => prev.map(msg => 
        msg.id === aiMessageId 
          ? { 
              ...msg, 
              content: `오류가 발생했습니다: ${error.message}\n\nAPI 키가 설정되지 않았거나 네트워크 오류일 수 있습니다. 설정에서 API 키를 확인해주세요.`,
              isStreaming: false
            }
          : msg
      ));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const copyToClipboard = async (text, index) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(-1), 2000);
    } catch (error) {
      console.error('복사 실패:', error);
    }
  };


  if (!isOpen) return null;

  return (
    <SidebarChatContainer
      initial={{ x: 350 }}
      animate={{ x: 0 }}
      exit={{ x: 350 }}
      transition={{ type: 'spring', damping: 20, stiffness: 300 }}
    >
      <ChatHeader>
        <ChatTitle>
          <Bot size={10} />
          <span>paper</span>
        </ChatTitle>
        <ChatControls>
          <ModeSelector>
            <ModeButton
              $active={mode === 'write'}
              onClick={() => onModeChange('write')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Edit3 size={12} />
              작성
            </ModeButton>
            <ModeButton
              $active={mode === 'edit'}
              onClick={() => onModeChange('edit')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Edit3 size={12} />
              수정
            </ModeButton>
            <ModeButton
              $active={mode === 'question'}
              onClick={() => onModeChange('question')}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <MessageCircle size={12} />
              질문
            </ModeButton>
          </ModeSelector>
          <ControlButton onClick={onMinimize} whileHover={{ scale: 1.1 }}>
            {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
          </ControlButton>
          <ControlButton onClick={onClose} whileHover={{ scale: 1.1 }}>
            <X size={16} />
          </ControlButton>
        </ChatControls>
      </ChatHeader>
      {false && (
        <SuggestionsBar theme={theme}>
          {examplePrompts.map((p, idx) => (
            <SuggestionButton
              key={idx}
              theme={theme}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => sendExample(p)}
            >
              {p}
            </SuggestionButton>
          ))}
        </SuggestionsBar>
      )}
      {isRenaming && (
        <div style={{ padding: '8px 12px', borderBottom: `1px solid ${theme.mode === 'dark' ? '#333333' : '#e1e4e8'}` }}>
          <input
            value={renameValue}
            onChange={(e) => setRenameValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') commitRename(); }}
            style={{ width: '75%', height: 28, marginRight: 8 }}
          />
          <button onClick={commitRename} style={{ height: 28 }}>저장</button>
          <button onClick={() => setIsRenaming(false)} style={{ height: 28, marginLeft: 6 }}>취소</button>
        </div>
      )}

      <ChatArea ref={chatAreaRef}>
        {messages.map((message, index) => (
          <MessageContainer
            key={message.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <MessageAvatar $isUser={message.type === 'user'}>
              {message.type === 'user' ? <User size={14} /> : <Bot size={14} />}
            </MessageAvatar>
            <MessageContent 
              $isUser={message.type === 'user'} 
              $isStreaming={message.isStreaming}
            >
              <div style={{ whiteSpace: 'pre-wrap' }}>{message.content}</div>
              {message.type === 'assistant' && (
                <MessageActions>
                  <ActionButton
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => copyToClipboard(message.content, index)}
                  >
                    {copiedIndex === index ? <Check size={12} /> : <Copy size={12} />}
                    {copiedIndex === index ? '복사됨' : '복사'}
                  </ActionButton>
                </MessageActions>
              )}
            </MessageContent>
          </MessageContainer>
        ))}
        
        {/* 논문 모드 진행 표시 */}
        {progress && (
          <ProgressContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
          >
            <ProgressHeader>
              <ProgressIcon>
                {progress.stage === 'part1' ? '1' : '2'}
              </ProgressIcon>
              <ProgressText>
                <ProgressTitle>{progress.message}</ProgressTitle>
                <ProgressDescription>
                  {(() => {
                    const model = (typeof window !== 'undefined' && localStorage.getItem('oneline_model')) || 'oneline-chat';
                    if (progress.stage === 'writing') {
                      return model === 'oneline-reasoner' ? '논문을 작성하고 있습니다...' : '보고서를 작성하고 있습니다...';
                    }
                    if (model === 'oneline-reasoner') {
                      return progress.stage === 'part1'
                        ? '논문의 구조와 배경, 방법론을 작성하고 있습니다...'
                        : '실험 결과와 결론을 작성하고 있습니다...';
                    }
                    return progress.stage === 'part1'
                      ? '보고서의 개요와 배경, 핵심 내용 전반부를 작성하고 있습니다...'
                      : '분석 및 결론을 작성하고 있습니다...';
                  })()}
                </ProgressDescription>
              </ProgressText>
            </ProgressHeader>
            
            <ProgressBar>
              <ProgressFill
                initial={{ width: 0 }}
                animate={{ 
                  width: progress.stage === 'part1' ? '50%' : '100%' 
                }}
                transition={{ duration: 0.5, ease: "easeOut" }}
              />
            </ProgressBar>
            
            <StageBadges>
              {(() => {
                const model = (typeof window !== 'undefined' && localStorage.getItem('oneline_model')) || 'oneline-chat';
                if (progress.stage === 'writing') {
                  return (
                    <StageBadge active>
                      {progress.status === 'complete' && <CheckIcon>✓</CheckIcon>}
                      {model === 'oneline-reasoner' ? '논문 작성' : '보고서 작성'}
                    </StageBadge>
                  );
                }
                return (
                  <>
                    <StageBadge active={progress.stage === 'part1' || progress.stage === 'part2'}>
                      {progress.stage === 'part2' && <CheckIcon>✓</CheckIcon>}
                      {(model === 'oneline-reasoner') ? '논문 구조' : '보고서 구조'}
                    </StageBadge>
                    <StageBadge active={progress.stage === 'part2'}>
                      {progress.stage === 'part2' && progress.status === 'complete' && <CheckIcon>✓</CheckIcon>}
                      {(model === 'oneline-reasoner') ? '실험 & 결론' : '분석 & 결론'}
                    </StageBadge>
                  </>
                );
              })()}
            </StageBadges>
          </ProgressContainer>
        )}
        
      </ChatArea>

      <InputArea>
        <InputContainer>
          <TextArea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder={
              mode === 'write' 
                ? '어떤 주제로 글을 작성할까요?' 
                : mode === 'edit' 
                ? '어떻게 수정할까요?' 
                : '무엇이 궁금하신가요?'
            }
            disabled={isLoading}
          />
          <SendButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
          >
            <Send size={14} />
          </SendButton>
        </InputContainer>
      </InputArea>
    </SidebarChatContainer>
  );
};

export default SidebarChat;
